import type { ShoppingListItem, ShoppingListItemState, ShoppingListItemSource } from './shopping-list-item.model';

/** Client-side sync state for a shopping-list item. */
export type SyncStatus = 'pending_sync' | 'synced' | 'failed';

/**
 * Local UI model that mirrors a server `ShoppingListItem` and adds
 * client-only fields used by the sync queue.
 *
 * Before the server has acknowledged the item, only `client_id`,
 * `raw_input`, `title` (= raw_input), `state` and `sync_status` are set.
 * After reconciliation, all server fields are populated and
 * `sync_status` becomes `'synced'`.
 */
export interface LocalShoppingListItem {
  // ── Identity ──────────────────────────────────────────────────────────
  /** Always present locally. */
  client_id: string;
  /** Assigned after the server creates the item. */
  id?: number;

  // ── Server-owned fields (populated after sync) ───────────────────────
  shopping_list_id?: number;
  source?: ShoppingListItemSource;
  /** Defaults to `'pending'` before sync. */
  state: ShoppingListItemState;
  /** Equals `raw_input` until the server returns the parsed title. */
  title: string;
  subtitle?: string | null;
  product_key?: string | null;
  product_id?: number | null;
  created_at?: string;
  updated_at?: string;

  // ── Local-only fields ────────────────────────────────────────────────
  sync_status: SyncStatus;
  /** Preserved verbatim so we can retry a failed POST. */
  raw_input: string;
  /** Human-readable error from the most recent failed sync attempt. */
  last_sync_error?: string;
}

/**
 * Merge a server item into a local one (server is source of truth for
 * synced fields). Preserves local `client_id` and `raw_input`.
 */
export function mergeServerIntoLocal(
  local: LocalShoppingListItem,
  server: ShoppingListItem,
): LocalShoppingListItem {
  return {
    ...local,
    id: server.id,
    shopping_list_id: server.shopping_list_id,
    source: server.source,
    state: server.state,
    title: server.title,
    subtitle: server.subtitle,
    product_key: server.product_key,
    product_id: server.product_id,
    created_at: server.created_at,
    updated_at: server.updated_at,
    sync_status: 'synced',
    last_sync_error: undefined,
    // keep client_id & raw_input as-is
    client_id: local.client_id,
    raw_input: local.raw_input,
  };
}

/**
 * Convert a server item into a fresh local item (used when polling
 * surfaces an item we don't have yet).
 */
export function localFromServer(server: ShoppingListItem): LocalShoppingListItem {
  return {
    client_id: server.client_id ?? `srv-${server.id}`,
    id: server.id,
    shopping_list_id: server.shopping_list_id,
    source: server.source,
    state: server.state,
    title: server.title,
    subtitle: server.subtitle,
    product_key: server.product_key,
    product_id: server.product_id,
    created_at: server.created_at,
    updated_at: server.updated_at,
    sync_status: 'synced',
    raw_input: server.title,
  };
}

